import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { store } from '../redux/Store';
import { logout } from '../redux/slices/authSlice';

// Get API URL from environment or use default
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies (refresh token)
});

// Request interceptor to add access token to headers
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If error is 401 and we haven't retried yet, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Call refresh endpoint (should send refreshToken cookie).
        // Canonical route is `/auth/refresh-token`; the server also
        // accepts `/auth/refresh` as a legacy alias for cached bundles
        // (auth re-audit 2026-07-04 N-13).
        const response = await axios.post(
          `${API_URL}/auth/refresh-token`,
          {},
          { withCredentials: true }
        );

        const { accessToken } = response.data;

        // Save new access token
        localStorage.setItem('accessToken', accessToken);

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens and dispatch logout
        // This will update Redux state and let React Router handle navigation
        store.dispatch(logout());
        // Note: Don't do window.location.href = '/login' as it causes a hard reload
        // React Router will handle the redirect via ProtectedRoute
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
